import { useMemo } from 'react'

import { nonEditableQuoteStatuses } from '@features/invoices/constants/nonEditableQuoteStatuses'
import { quoteSentStatusEvents } from '@features/invoices/constants/quoteSentStatusEvents'
import { QuoteActionType } from '@features/invoices/enums/quoteActionType'
import { QuoteStatus } from '@features/invoices/enums/quoteStatus'
import { getQuoteConvertEligibility } from '@features/invoices/utils/depositInvoices/getQuoteConvertEligibility'
import { getQuoteConvertEligibilityData } from '@features/invoices/utils/depositInvoices/getQuoteConvertEligibilityData'
import { isQuoteConvertibleToInvoice } from '@features/invoices/utils/depositInvoices/isQuoteConvertibleToInvoice'
import { getQuoteMarkabilityStatus } from '@features/invoices/utils/getQuoteMarkabilityStatus'

import { useQuote } from '../../../contexts/quoteContext'

const ACTION_BUTTONS_DISPLAY_LIMIT = 2

export const useQuoteViewActions = () => {
  const { quote } = useQuote()
  const quoteStatus = quote?.status?.status
  const hasSentEvents = quote?.status?.events?.some((event) => quoteSentStatusEvents.includes(event.eventType))
  const { isMarkableAsSent, isMarkableAsUnsent } = getQuoteMarkabilityStatus(quoteStatus, hasSentEvents)
  const convertEligibility = getQuoteConvertEligibility(getQuoteConvertEligibilityData(quote))
  const isConvertibleToInvoice = isQuoteConvertibleToInvoice(convertEligibility)
  const isEditable = !!quoteStatus && !nonEditableQuoteStatuses.includes(quoteStatus)

  const isInvoiced = quoteStatus === QuoteStatus.Invoiced

  const isDeletable = quoteStatus !== QuoteStatus.Deleted && !isInvoiced

  const quoteViewActions: QuoteActionType[] = useMemo(() => {
    return [
      ...(isConvertibleToInvoice ? [QuoteActionType.ConvertToInvoice] : []),
      QuoteActionType.Share,
      ...(isEditable ? [QuoteActionType.Edit] : []),
      ...(isMarkableAsSent ? [QuoteActionType.MarkAsSent] : []),
      ...(isMarkableAsUnsent ? [QuoteActionType.MarkAsUnsent] : []),
      ...(isDeletable ? [QuoteActionType.Delete] : []),
      QuoteActionType.Duplicate,
    ]
  }, [isConvertibleToInvoice, isDeletable, isEditable, isMarkableAsSent, isMarkableAsUnsent])

  const quoteActionsDisplayedAsButtons = useMemo(
    () => quoteViewActions.slice(0, ACTION_BUTTONS_DISPLAY_LIMIT),
    [quoteViewActions],
  )

  const quoteActionsDisplayedAsMenuItems = useMemo(
    () => quoteViewActions.slice(ACTION_BUTTONS_DISPLAY_LIMIT),
    [quoteViewActions],
  )

  return { quoteActionsDisplayedAsButtons, quoteActionsDisplayedAsMenuItems }
}
